/**
 * getVisibleVideoRect — helper puro que calcula el bounding box en pixels
 * del video real visible dentro de un contenedor, según el `object-fit`
 * aplicado. Base para dibujar overlays alineados con el video (skeleton,
 * bounding boxes, etc.) sin desfase cuando el contenedor y el stream
 * tienen aspect ratios distintos.
 *
 * Los landmarks de MediaPipe son [0..1] sobre el FRAME REAL del stream
 * (típicamente 640×480, aspect 4:3 según getUserMedia). Al proyectar a
 * pixels del canvas usando naïve `landmark.x * canvas.width`, el skeleton
 * queda desfasado si:
 *   - object-contain: hay letterbox negro y el video ocupa solo parte
 *     del canvas.
 *   - object-cover: el video se desborda (crop) y sale del canvas por
 *     bordes.
 *
 * Esta función retorna el rect efectivo del video → landmark → pixel se
 * hace con:
 *   pixel_x = rect.x + landmark.x * rect.w
 *   pixel_y = rect.y + landmark.y * rect.h
 *
 * Función pura sobre números — sin DOM. Testeable directamente.
 */

export type ObjectFit = "contain" | "cover";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface VisibleVideoRectInput {
  /** Dimensiones intrínsecas del stream (video.videoWidth / videoHeight). */
  videoWidth: number;
  videoHeight: number;
  /** Dimensiones del contenedor donde el video se renderea (px CSS). */
  containerWidth: number;
  containerHeight: number;
  /** Cómo el video se ajusta al contenedor. Corresponde a la propiedad
   *  CSS object-fit del elemento <video>. */
  fit: ObjectFit;
}

/** Calcula el rect en px del video visible dentro del contenedor.
 *
 *  Para `contain`: rect siempre entra dentro del contenedor (x, y ≥ 0);
 *  el sobrante es letterbox negro.
 *
 *  Para `cover`: rect puede tener x/y NEGATIVOS y w/h > container (el
 *  video se desborda por los bordes que se cropean). Landmarks que caen
 *  fuera del [0..containerW/H] simplemente se dibujan afuera y quedan
 *  clippeados por el `overflow-hidden` del wrapper — ningún caller
 *  necesita lógica extra.
 *
 *  Inputs inválidos (dimensiones ≤ 0) → rect vacío (evita división por 0
 *  y NaNs).
 */
export function getVisibleVideoRect(input: VisibleVideoRectInput): Rect {
  const { videoWidth, videoHeight, containerWidth, containerHeight, fit } =
    input;
  if (
    videoWidth <= 0 ||
    videoHeight <= 0 ||
    containerWidth <= 0 ||
    containerHeight <= 0
  ) {
    return { x: 0, y: 0, w: 0, h: 0 };
  }
  const scaleX = containerWidth / videoWidth;
  const scaleY = containerHeight / videoHeight;
  const scale = fit === "contain" ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY);
  const w = videoWidth * scale;
  const h = videoHeight * scale;
  const x = (containerWidth - w) / 2;
  const y = (containerHeight - h) / 2;
  return { x, y, w, h };
}
